import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  HTMLAttributes,
  HTMLProps
} from 'react';
import {
  FiAlertCircle
} from 'react-icons/fi'
import {
  Button,
  OrangeBox,
  Input,
  LabelsMap
} from 'components';
import {
  useConfigContext,
  useOverlayContext,
  useSensorsStore,
  useUserDataContext
} from 'context';
import {
  usePrevious
} from 'hooks';
import {
  FlexContainer,
  Flex4,
  Flex6,
  Flex8
} from 'layout';
import {
  CellMonitor,
  RecentCellActivityGraph,
  RecentActivityBucketsGraph,
  PassiveMonitor,
  TMSITracking
} from 'elements';
import {
  HelpCircle
} from 'theme/icons';
import styled from 'styled-components';
import {
  dataObserverListeners
} from 'hooks';
import {
  v4 as uuid
} from 'uuid';

export const Landing: React.FC = () => {
  const [tmsiOnlyRecentActivity, setTmsiOnlyRecentActivity] = useState<boolean>(false);
  const {attachTooltip} = useOverlayContext();
  const {showPopup} = useOverlayContext();
  const [demoButtonVisible, setDemoButtonVisible] = useState<boolean>(false);
  const {
    updateUserDataMixins
  } = useUserDataContext();
  // Memos
  const {
    startTimestamp,
  } = useMemo(() => ({
    startTimestamp: Date.now()-24*60*60*1000,
    endTimestamp: Date.now()
  }),[]);

  // Demo mode listener
  useEffect(function DemoSimulator(){
    window.addEventListener('keydown', e => {
      if(e.metaKey && e.key === '9'){
        setDemoButtonVisible(prev => !prev);
      }
    })
  },[]);
  const handleDemoClick = useCallback(async () => {
    const newMixin = {
      type: 'cell' as const,
      identifier: '',
      power: -30,
      cellProvider: ['Verizon Wireless', 'At&t Mobility', 'T-Mobile USA', 'unknown'][Math.round(Math.random()*3)],
      timestamp: Date.now(),
      establishmentCause: 'emergency'
    }
    const uid = uuid();

    // simulate posting to mqtt topic
    dataObserverListeners.fire([newMixin]);
    // create a mixin record and preserve to firebase
    await updateUserDataMixins({[uid]: newMixin} as any);
    console.log('[Landing][DemoSimulator] successfully saved user data');
  },[
    updateUserDataMixins
  ]);

  return (
    <Container>
      <WarningsDisplay />

      <div>
        <h1>Real-time TMSI Monitor</h1>
        <h3>Cellular traffic near you in real-time</h3>
        <CellMonitor>
          {demoButtonVisible ?
            <Button variant='secondary' sizeVariant='small' style={{
              marginLeft: '1rem'
            }} onClick={handleDemoClick}>demo</Button>
          : null}
        </CellMonitor>
      </div>

      <div className="tmsi-waterflow-section">
        <OrangeBox >
          <div className="float-right">
            <LabelsMap />
          
            <div className="quick-links">
              <a 
                onClick={() => showPopup({
                  dismissable: true,
                  children: <HowToReadChartPopup />
                })} 
                href="#how-to-read-tmsi-waterfall-chart">How to read this chart <HelpCircle /></a>
            </div>
          </div>

          <h1>TMSI Tracking <QuestionMarkIcon ref={(ref) => attachTooltip(ref, <TMSITrackingTooltip />, {
            shiftOffset: 132
          })} /></h1>
          <h3>Temporary Mobile Subscriber Identity numbers over time.</h3>
          
          <TMSITracking
            live
            width={1325}
            minHeight={268}
            variant='scroll'
            markEachNMilliseconds={3*60*60*1000} //mark every 3 hours
            marksFramePaddingMillisecondsLeft={3*60*60*1000} // no closer then 3 hours
            marksFramePaddingMillisecondsRight={1.5*60*60*1000}
            axisLabel="Last 24 Hours"
            startTimestamp={startTimestamp}
            showNow
            />
        </OrangeBox>
      </div>

      <FlexContainer className="flex-section">
        <Flex6>
          <OrangeBox>
            <OrangeBoxToggle>All Scans <Input type="switch" onChange={(e) => {setTmsiOnlyRecentActivity(e.target.checked)}} /> TMSI</OrangeBoxToggle>
            <h1>Carrier Activity</h1>
            <h3>Today’s recent cellular traffic</h3>
            <RecentCellActivityGraph tmsiOnly={tmsiOnlyRecentActivity} />
          </OrangeBox>
        </Flex6>
        <Flex6>
          <OrangeBox>
            <h1>Daily Carrier Volume</h1>
            <h3>Cellular traffic totals by day</h3>
            <RecentActivityBucketsGraph />
          </OrangeBox>
        </Flex6>
      </FlexContainer>

      <div>
        <h1>WiFi and BlueTooth Activity</h1>
        <h3>WiFi and BlueTooth devices your sensor is detecting</h3>
        <PassiveMonitor />
      </div>
    </Container>
  );
};

const QuestionMarkIcon = styled(
  React.forwardRef<HTMLDivElement, ({className?: string} & HTMLAttributes<HTMLDivElement>)>(({className, ...rest}, ref) => (
    <div ref={ref} className={(className||'')} {...rest}>
      <HelpCircle strokeWidth='2'/>
    </div>
  ))
  )`${({theme})=> `
  vertical-align: middle;
  cursor: pointer;
  display: inline-block;
  color: ${theme.newColors.OrangeDream};
`}`;

const TMSITrackingTooltip  = styled(({className}) => (
  <p className={className||''}>TMSI stands for Temporary Mobile Subscriber Identity. It allows your cellular device to access a cellular network. TMSI’s are sent with certain cell scans as the device connects to the cellular networks.</p>
  ))`${({theme}) => `
  max-width: 330px;
  margin: 0px;

  ${theme.fontStyles.captionPlaceholder}
`}`;

const HowToReadChartPopup = styled( 
  ({className}:{className?:string}&HTMLProps<HTMLDivElement>) => (
    <div className={className||''}>
      <h2>How to read the TMSI chart</h2>
      <h2>Tracking TMSI over time</h2>
      <p>Each time your sensor captures the same TMSI we plot that capture the time. We represent that capture as a dot on the timeline.</p>
      <p>This view shows you the interval of TMSI captures over time so you can see how frequently a TMSI is captured and infer a period of time the device associated with that TMSI was near your sensor location.</p>

      <h2>Representing Signal Strength</h2>
      <p>The brightness of each dot represents the strength of the signal at the time the TMSI was captured.</p>
      <p>Signal strength may be an indication of proximity to your sensor and location.</p>
      
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={577}
        height={74}
        fill="none"
      >
        <rect width={577} height={74} fill="#FCEDCF" rx={3} />
        <path
          fill="#000"
          d="M43.888 28v-8.4h1.008v3.708h4.356V19.6h1.008V28h-1.008v-3.864h-4.356V28h-1.008Zm8.433-7.272a.707.707 0 0 1-.505-.192.707.707 0 0 1-.192-.504c0-.192.064-.352.192-.48a.707.707 0 0 1 .505-.192c.192 0 .355.064.492.192a.632.632 0 0 1 .203.48c0 .2-.067.368-.203.504a.692.692 0 0 1-.492.192ZM51.816 28v-5.952h1.008V28h-1.008Zm4.852-1.872a3.07 3.07 0 0 1-.924-.132l-.6.564c.088.064.196.12.324.168.136.04.328.08.576.12.248.032.596.068 1.044.108.8.056 1.372.24 1.716.552.344.312.516.72.516 1.224 0 .344-.096.672-.288.984-.184.312-.472.568-.864.768-.384.2-.88.3-1.488.3a4.356 4.356 0 0 1-1.428-.216c-.416-.136-.74-.348-.972-.636-.232-.28-.348-.636-.348-1.068 0-.224.06-.468.18-.732.12-.256.344-.5.672-.732a3.022 3.022 0 0 1-.456-.228 2.898 2.898 0 0 1-.336-.288v-.276l1.02-1.008c-.472-.4-.708-.928-.708-1.584 0-.392.092-.748.276-1.068.184-.32.452-.572.804-.756.352-.192.78-.288 1.284-.288.344 0 .656.048.936.144H59.8v.756l-1.116.048c.232.336.348.724.348 1.164 0 .392-.096.748-.288 1.068-.184.32-.452.576-.804.768-.344.184-.768.276-1.272.276Zm0-.828c.424 0 .76-.108 1.008-.324.256-.224.384-.544.384-.96 0-.408-.128-.72-.384-.936-.248-.224-.584-.336-1.008-.336-.432 0-.776.112-1.032.336-.248.216-.372.528-.372.936 0 .416.124.736.372.96.256.216.6.324 1.032.324Zm-1.752 3.468c0 .4.168.696.504.888.336.2.756.3 1.26.3.496 0 .892-.108 1.188-.324a1.01 1.01 0 0 0 .456-.864.888.888 0 0 0-.312-.672c-.208-.184-.592-.292-1.152-.324-.44-.032-.82-.072-1.14-.12-.328.176-.544.364-.648.564-.104.208-.156.392-.156.552ZM60.673 28v-8.64h1.008v3.708c.2-.368.484-.652.852-.852a2.398 2.398 0 0 1 1.2-.312c.688 0 1.24.216 1.656.648.416.424.624 1.08.624 1.968V28h-.996v-3.372c0-1.24-.5-1.86-1.5-1.86-.52 0-.956.188-1.308.564-.352.368-.528.896-.528 1.584V28h-1.008Zm9.279.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632a2.667 2.667 0 0 1 1.032-1.092c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456ZM73.844 28v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm9.743.144c-.568 0-1.08-.128-1.536-.384a2.916 2.916 0 0 1-1.068-1.092c-.256-.472-.384-1.02-.384-1.644 0-.624.128-1.168.384-1.632.264-.472.62-.836 1.068-1.092a3.01 3.01 0 0 1 1.536-.396c.704 0 1.296.184 1.776.552.488.368.796.86.924 1.476h-1.032a1.301 1.301 0 0 0-.588-.852 1.922 1.922 0 0 0-1.092-.312 1.89 1.89 0 0 0-.948.252 1.88 1.88 0 0 0-.72.756c-.184.336-.276.752-.276 1.248s.092.912.276 1.248c.184.336.424.592.72.768.296.168.612.252.948.252.416 0 .78-.1 1.092-.3.312-.208.508-.5.588-.876h1.032c-.12.6-.424 1.088-.912 1.464-.488.376-1.084.564-1.788.564Zm5.824 0c-.496 0-.908-.084-1.236-.252a1.713 1.713 0 0 1-.972-1.584c0-.608.232-1.076.696-1.404.464-.328 1.096-.492 1.896-.492h1.608v-.072c0-.52-.136-.912-.408-1.176-.272-.272-.636-.408-1.092-.408-.392 0-.732.1-1.02.3-.28.192-.456.476-.528.852h-1.032c.04-.432.184-.796.432-1.092.256-.296.572-.52.948-.672.376-.16.776-.24 1.2-.24.832 0 1.456.224 1.872.672.424.44.636 1.028.636 1.764V28h-.9l-.06-1.068a2.39 2.39 0 0 1-.744.864c-.32.232-.752.348-1.296.348Zm.156-.852c.384 0 .712-.1.984-.3.28-.2.492-.46.636-.78a2.43 2.43 0 0 0 .216-1.008v-.012h-1.524c-.592 0-1.012.104-1.26.312-.24.2-.36.452-.36.756 0 .312.112.564.336.756.232.184.556.276.972.276Zm4.092 3.348v-8.592h.912l.096.984c.192-.288.464-.548.816-.78.36-.232.816-.348 1.368-.348.592 0 1.108.136 1.548.408.44.272.78.644 1.02 1.116.248.472.372 1.008.372 1.608 0 .6-.124 1.136-.372 1.608-.24.464-.584.832-1.032 1.104-.44.264-.956.396-1.548.396-.488 0-.924-.1-1.308-.3-.376-.2-.664-.48-.864-.84v3.636h-1.008Zm3.072-3.372c.4 0 .752-.092 1.056-.276.304-.192.544-.456.72-.792.176-.336.264-.728.264-1.176 0-.448-.088-.84-.264-1.176a1.942 1.942 0 0 0-.72-.78 1.935 1.935 0 0 0-1.056-.288c-.4 0-.752.096-1.056.288a1.942 1.942 0 0 0-.72.78c-.176.336-.264.728-.264 1.176 0 .448.088.84.264 1.176.176.336.416.6.72.792.304.184.656.276 1.056.276Zm6.317.732c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm4.196.144c-.704 0-1.264-.212-1.68-.636-.416-.432-.624-1.092-.624-1.98v-3.48h1.008v3.372c0 1.24.508 1.86 1.524 1.86.52 0 .948-.184 1.284-.552.344-.376.516-.908.516-1.596v-3.084h1.008V28h-.912l-.072-1.068c-.184.376-.46.672-.828.888-.36.216-.768.324-1.224.324Zm4.432-.144v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm6.548.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632A2.667 2.667 0 0 1 116.7 22.3c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456Zm6.592 5.388c-.592 0-1.112-.136-1.56-.408a2.835 2.835 0 0 1-1.032-1.116c-.24-.472-.36-1.008-.36-1.608 0-.6.124-1.132.372-1.596a2.75 2.75 0 0 1 1.032-1.104c.44-.272.96-.408 1.56-.408.488 0 .92.1 1.296.3.376.2.668.48.876.84V19.36h1.008V28h-.912l-.096-.984c-.192.288-.468.548-.828.78-.36.232-.812.348-1.356.348Zm.108-.876c.4 0 .752-.092 1.056-.276.312-.192.552-.456.72-.792.176-.336.264-.728.264-1.176 0-.448-.088-.84-.264-1.176a1.837 1.837 0 0 0-.72-.78 1.935 1.935 0 0 0-1.056-.288c-.392 0-.744.096-1.056.288a1.942 1.942 0 0 0-.72.78c-.168.336-.252.728-.252 1.176 0 .448.084.84.252 1.176.176.336.416.6.72.792a2.04 2.04 0 0 0 1.056.276Zm9.655.876c-.712 0-1.304-.18-1.776-.54-.472-.36-.748-.848-.828-1.464h1.032c.064.312.228.584.492.816.272.224.636.336 1.092.336.424 0 .736-.088.936-.264.2-.184.3-.4.3-.648 0-.36-.132-.6-.396-.72-.256-.12-.62-.228-1.092-.324a6.189 6.189 0 0 1-.96-.276 2.221 2.221 0 0 1-.804-.504c-.216-.224-.324-.516-.324-.876 0-.52.192-.944.576-1.272.392-.336.92-.504 1.584-.504.632 0 1.148.16 1.548.48.408.312.644.76.708 1.344h-.996a1.008 1.008 0 0 0-.396-.708c-.216-.176-.508-.264-.876-.264-.36 0-.64.076-.84.228a.728.728 0 0 0-.288.6c0 .24.124.428.372.564.256.136.6.252 1.032.348.368.08.716.18 1.044.3.336.112.608.284.816.516.216.224.324.552.324.984.008.536-.196.98-.612 1.332-.408.344-.964.516-1.668.516Zm4.068-7.416a.707.707 0 0 1-.504-.192.707.707 0 0 1-.192-.504c0-.192.064-.352.192-.48a.707.707 0 0 1 .504-.192c.192 0 .356.064.492.192a.632.632 0 0 1 .204.48c0 .2-.068.368-.204.504a.692.692 0 0 1-.492.192ZM138.143 28v-5.952h1.008V28h-1.008Zm4.851-1.872a3.07 3.07 0 0 1-.924-.132l-.6.564c.088.064.196.12.324.168.136.04.328.08.576.12.248.032.596.068 1.044.108.8.056 1.372.24 1.716.552.344.312.516.72.516 1.224 0 .344-.096.672-.288.984-.184.312-.472.568-.864.768-.384.2-.88.3-1.488.3a4.356 4.356 0 0 1-1.428-.216c-.416-.136-.74-.348-.972-.636-.232-.28-.348-.636-.348-1.068 0-.224.06-.468.18-.732.12-.256.344-.5.672-.732a3.022 3.022 0 0 1-.456-.228 2.898 2.898 0 0 1-.336-.288v-.276l1.02-1.008c-.472-.4-.708-.928-.708-1.584 0-.392.092-.748.276-1.068.184-.32.452-.572.804-.756.352-.192.78-.288 1.284-.288.344 0 .656.048.936.144h2.196v.756l-1.116.048c.232.336.348.724.348 1.164 0 .392-.096.748-.288 1.068-.184.32-.452.576-.804.768-.344.184-.768.276-1.272.276Zm0-.828c.424 0 .76-.108 1.008-.324.256-.224.384-.544.384-.96 0-.408-.128-.72-.384-.936-.248-.224-.584-.336-1.008-.336-.432 0-.776.112-1.032.336-.248.216-.372.528-.372.936 0 .416.124.736.372.96.256.216.6.324 1.032.324Zm-1.752 3.468c0 .4.168.696.504.888.336.2.756.3 1.26.3.496 0 .892-.108 1.188-.324a1.01 1.01 0 0 0 .456-.864.888.888 0 0 0-.312-.672c-.208-.184-.592-.292-1.152-.324-.44-.032-.82-.072-1.14-.12-.328.176-.544.364-.648.564-.104.208-.156.392-.156.552ZM147 28v-5.952h.912l.06 1.068c.192-.376.468-.672.828-.888.36-.216.768-.324 1.224-.324.704 0 1.264.216 1.68.648.424.424.636 1.08.636 1.968V28h-1.008v-3.372c0-1.24-.512-1.86-1.536-1.86-.512 0-.94.188-1.284.564-.336.368-.504.896-.504 1.584V28H147Zm8.583.144c-.496 0-.908-.084-1.236-.252a1.713 1.713 0 0 1-.972-1.584c0-.608.232-1.076.696-1.404.464-.328 1.096-.492 1.896-.492h1.608v-.072c0-.52-.136-.912-.408-1.176-.272-.272-.636-.408-1.092-.408-.392 0-.732.1-1.02.3-.28.192-.456.476-.528.852h-1.032c.04-.432.184-.796.432-1.092.256-.296.572-.52.948-.672.376-.16.776-.24 1.2-.24.832 0 1.456.224 1.872.672.424.44.636 1.028.636 1.764V28h-.9l-.06-1.068a2.39 2.39 0 0 1-.744.864c-.32.232-.752.348-1.296.348Zm.156-.852c.384 0 .712-.1.984-.3.28-.2.492-.46.636-.78a2.43 2.43 0 0 0 .216-1.008v-.012h-1.524c-.592 0-1.012.104-1.26.312-.24.2-.36.452-.36.756 0 .312.112.564.336.756.232.184.556.276.972.276Zm4.092.708v-8.64h1.008V28h-1.008Zm7.588.144c-.712 0-1.304-.18-1.776-.54-.472-.36-.748-.848-.828-1.464h1.032c.064.312.228.584.492.816.272.224.636.336 1.092.336.424 0 .736-.088.936-.264.2-.184.3-.4.3-.648 0-.36-.132-.6-.396-.72-.256-.12-.62-.228-1.092-.324a6.189 6.189 0 0 1-.96-.276 2.221 2.221 0 0 1-.804-.504c-.216-.224-.324-.516-.324-.876 0-.52.192-.944.576-1.272.392-.336.92-.504 1.584-.504.632 0 1.148.16 1.548.48.408.312.644.76.708 1.344h-.996a1.008 1.008 0 0 0-.396-.708c-.216-.176-.508-.264-.876-.264-.36 0-.64.076-.84.228a.728.728 0 0 0-.288.6c0 .24.124.428.372.564.256.136.6.252 1.032.348.368.08.716.18 1.044.3.336.112.608.284.816.516.216.224.324.552.324.984.008.536-.196.98-.612 1.332-.408.344-.964.516-1.668.516Zm5.538-.144c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm2.025 0v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm6.549.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632a2.667 2.667 0 0 1 1.032-1.092c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456ZM185.423 28v-5.952h.912l.06 1.068c.192-.376.468-.672.828-.888.36-.216.768-.324 1.224-.324.704 0 1.264.216 1.68.648.424.424.636 1.08.636 1.968V28h-1.008v-3.372c0-1.24-.512-1.86-1.536-1.86-.512 0-.94.188-1.284.564-.336.368-.504.896-.504 1.584V28h-1.008Zm8.955-1.872a3.07 3.07 0 0 1-.924-.132l-.6.564c.088.064.196.12.324.168.136.04.328.08.576.12.248.032.596.068 1.044.108.8.056 1.372.24 1.716.552.344.312.516.72.516 1.224 0 .344-.096.672-.288.984-.184.312-.472.568-.864.768-.384.2-.88.3-1.488.3a4.356 4.356 0 0 1-1.428-.216c-.416-.136-.74-.348-.972-.636-.232-.28-.348-.636-.348-1.068 0-.224.06-.468.18-.732.12-.256.344-.5.672-.732a3.022 3.022 0 0 1-.456-.228 2.898 2.898 0 0 1-.336-.288v-.276l1.02-1.008c-.472-.4-.708-.928-.708-1.584 0-.392.092-.748.276-1.068.184-.32.452-.572.804-.756.352-.192.78-.288 1.284-.288.344 0 .656.048.936.144h2.196v.756l-1.116.048c.232.336.348.724.348 1.164 0 .392-.096.748-.288 1.068-.184.32-.452.576-.804.768-.344.184-.768.276-1.272.276Zm0-.828c.424 0 .76-.108 1.008-.324.256-.224.384-.544.384-.96 0-.408-.128-.72-.384-.936-.248-.224-.584-.336-1.008-.336-.432 0-.776.112-1.032.336-.248.216-.372.528-.372.936 0 .416.124.736.372.96.256.216.6.324 1.032.324Zm-1.752 3.468c0 .4.168.696.504.888.336.2.756.3 1.26.3.496 0 .892-.108 1.188-.324a1.01 1.01 0 0 0 .456-.864.888.888 0 0 0-.312-.672c-.208-.184-.592-.292-1.152-.324-.44-.032-.82-.072-1.14-.12-.328.176-.544.364-.648.564-.104.208-.156.392-.156.552Zm8.109-.768c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm2.025 0v-8.64h1.008v3.708c.2-.368.484-.652.852-.852a2.398 2.398 0 0 1 1.2-.312c.688 0 1.24.216 1.656.648.416.424.624 1.08.624 1.968V28h-.996v-3.372c0-1.24-.5-1.86-1.5-1.86-.52 0-.956.188-1.308.564-.352.368-.528.896-.528 1.584V28h-1.008ZM386.888 28v-8.4h1.008v7.596h3.912V28h-4.92Zm8.435.144c-.56 0-1.064-.128-1.512-.384a2.827 2.827 0 0 1-1.068-1.08c-.256-.472-.384-1.024-.384-1.656 0-.632.132-1.18.396-1.644.264-.472.62-.836 1.068-1.092a3.06 3.06 0 0 1 1.524-.384c.56 0 1.064.128 1.512.384.448.256.8.62 1.056 1.092.264.464.396 1.012.396 1.644 0 .632-.132 1.184-.396 1.656a2.802 2.802 0 0 1-1.08 1.08 2.993 2.993 0 0 1-1.512.384Zm0-.864c.344 0 .664-.084.96-.252a1.88 1.88 0 0 0 .72-.756c.184-.336.276-.752.276-1.248s-.092-.912-.276-1.248a1.803 1.803 0 0 0-.708-.756 1.89 1.89 0 0 0-.948-.252c-.344 0-.664.084-.96.252a1.88 1.88 0 0 0-.72.756c-.184.336-.276.752-.276 1.248s.092.912.276 1.248c.184.336.42.588.708.756.296.168.612.252.948.252Zm5.103.72-1.74-5.952h1.008l1.26 4.668 1.392-4.668h1.14l1.404 4.668 1.248-4.668h1.02L405.418 28h-1.032l-1.464-4.908L401.458 28h-1.032Zm10.036.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632a2.667 2.667 0 0 1 1.032-1.092c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456ZM414.354 28v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm9.743.144c-.568 0-1.08-.128-1.536-.384a2.916 2.916 0 0 1-1.068-1.092c-.256-.472-.384-1.02-.384-1.644 0-.624.128-1.168.384-1.632.264-.472.62-.836 1.068-1.092a3.01 3.01 0 0 1 1.536-.396c.704 0 1.296.184 1.776.552.488.368.796.86.924 1.476h-1.032a1.301 1.301 0 0 0-.588-.852 1.922 1.922 0 0 0-1.092-.312 1.89 1.89 0 0 0-.948.252 1.88 1.88 0 0 0-.72.756c-.184.336-.276.752-.276 1.248s.092.912.276 1.248c.184.336.424.592.72.768.296.168.612.252.948.252.416 0 .78-.1 1.092-.3.312-.208.508-.5.588-.876h1.032c-.12.6-.424 1.088-.912 1.464-.488.376-1.084.564-1.788.564Zm5.824 0c-.496 0-.908-.084-1.236-.252a1.713 1.713 0 0 1-.972-1.584c0-.608.232-1.076.696-1.404.464-.328 1.096-.492 1.896-.492h1.608v-.072c0-.52-.136-.912-.408-1.176-.272-.272-.636-.408-1.092-.408-.392 0-.732.1-1.02.3-.28.192-.456.476-.528.852h-1.032c.04-.432.184-.796.432-1.092.256-.296.572-.52.948-.672.376-.16.776-.24 1.2-.24.832 0 1.456.224 1.872.672.424.44.636 1.028.636 1.764V28h-.9l-.06-1.068a2.39 2.39 0 0 1-.744.864c-.32.232-.752.348-1.296.348Zm.156-.852c.384 0 .712-.1.984-.3.28-.2.492-.46.636-.78a2.43 2.43 0 0 0 .216-1.008v-.012h-1.524c-.592 0-1.012.104-1.26.312-.24.2-.36.452-.36.756 0 .312.112.564.336.756.232.184.556.276.972.276Zm4.092 3.348v-8.592h.912l.096.984c.192-.288.464-.548.816-.78.36-.232.816-.348 1.368-.348.592 0 1.108.136 1.548.408.44.272.78.644 1.02 1.116.248.472.372 1.008.372 1.608 0 .6-.124 1.136-.372 1.608-.24.464-.584.832-1.032 1.104-.44.264-.956.396-1.548.396-.488 0-.924-.1-1.308-.3-.376-.2-.664-.48-.864-.84v3.636h-1.008Zm3.072-3.372c.4 0 .752-.092 1.056-.276.304-.192.544-.456.72-.792.176-.336.264-.728.264-1.176 0-.448-.088-.84-.264-1.176a1.942 1.942 0 0 0-.72-.78 1.935 1.935 0 0 0-1.056-.288c-.4 0-.752.096-1.056.288a1.942 1.942 0 0 0-.72.78c-.176.336-.264.728-.264 1.176 0 .448.088.84.264 1.176.176.336.416.6.72.792.304.184.656.276 1.056.276Zm6.317.732c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm4.197.144c-.704 0-1.264-.212-1.68-.636-.416-.432-.624-1.092-.624-1.98v-3.48h1.008v3.372c0 1.24.508 1.86 1.524 1.86.52 0 .948-.184 1.284-.552.344-.376.516-.908.516-1.596v-3.084h1.008V28h-.912l-.072-1.068c-.184.376-.46.672-.828.888-.36.216-.768.324-1.224.324Zm4.431-.144v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm6.548.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632a2.667 2.667 0 0 1 1.032-1.092c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456Zm6.592 5.388c-.592 0-1.112-.136-1.56-.408a2.835 2.835 0 0 1-1.032-1.116c-.24-.472-.36-1.008-.36-1.608 0-.6.124-1.132.372-1.596a2.75 2.75 0 0 1 1.032-1.104c.44-.272.96-.408 1.56-.408.488 0 .92.1 1.296.3.376.2.668.48.876.84V19.36h1.008V28h-.912l-.096-.984c-.192.288-.468.548-.828.78-.36.232-.812.348-1.356.348Zm.108-.876c.4 0 .752-.092 1.056-.276.312-.192.552-.456.72-.792.176-.336.264-.728.264-1.176 0-.448-.088-.84-.264-1.176a1.837 1.837 0 0 0-.72-.78 1.935 1.935 0 0 0-1.056-.288c-.392 0-.744.096-1.056.288a1.942 1.942 0 0 0-.72.78c-.168.336-.252.728-.252 1.176 0 .448.084.84.252 1.176.176.336.416.6.72.792a2.04 2.04 0 0 0 1.056.276Zm9.655.876c-.712 0-1.304-.18-1.776-.54-.472-.36-.748-.848-.828-1.464h1.032c.064.312.228.584.492.816.272.224.636.336 1.092.336.424 0 .736-.088.936-.264.2-.184.3-.4.3-.648 0-.36-.132-.6-.396-.72-.256-.12-.62-.228-1.092-.324a6.189 6.189 0 0 1-.96-.276 2.221 2.221 0 0 1-.804-.504c-.216-.224-.324-.516-.324-.876 0-.52.192-.944.576-1.272.392-.336.92-.504 1.584-.504.632 0 1.148.16 1.548.48.408.312.644.76.708 1.344h-.996a1.008 1.008 0 0 0-.396-.708c-.216-.176-.508-.264-.876-.264-.36 0-.64.076-.84.228a.728.728 0 0 0-.288.6c0 .24.124.428.372.564.256.136.6.252 1.032.348.368.08.716.18 1.044.3.336.112.608.284.816.516.216.224.324.552.324.984.008.536-.196.98-.612 1.332-.408.344-.964.516-1.668.516Zm4.068-7.416a.707.707 0 0 1-.504-.192.707.707 0 0 1-.192-.504c0-.192.064-.352.192-.48a.707.707 0 0 1 .504-.192c.192 0 .356.064.492.192a.632.632 0 0 1 .204.48c0 .2-.068.368-.204.504a.692.692 0 0 1-.492.192ZM478.653 28v-5.952h1.008V28h-1.008Zm4.852-1.872a3.07 3.07 0 0 1-.924-.132l-.6.564c.088.064.196.12.324.168.136.04.328.08.576.12.248.032.596.068 1.044.108.8.056 1.372.24 1.716.552.344.312.516.72.516 1.224 0 .344-.096.672-.288.984-.184.312-.472.568-.864.768-.384.2-.88.3-1.488.3a4.356 4.356 0 0 1-1.428-.216c-.416-.136-.74-.348-.972-.636-.232-.28-.348-.636-.348-1.068 0-.224.06-.468.18-.732.12-.256.344-.5.672-.732a3.022 3.022 0 0 1-.456-.228 2.898 2.898 0 0 1-.336-.288v-.276l1.02-1.008c-.472-.4-.708-.928-.708-1.584 0-.392.092-.748.276-1.068.184-.32.452-.572.804-.756.352-.192.78-.288 1.284-.288.344 0 .656.048.936.144h2.196v.756l-1.116.048c.232.336.348.724.348 1.164 0 .392-.096.748-.288 1.068-.184.32-.452.576-.804.768-.344.184-.768.276-1.272.276Zm0-.828c.424 0 .76-.108 1.008-.324.256-.224.384-.544.384-.96 0-.408-.128-.72-.384-.936-.248-.224-.584-.336-1.008-.336-.432 0-.776.112-1.032.336-.248.216-.372.528-.372.936 0 .416.124.736.372.96.256.216.6.324 1.032.324Zm-1.752 3.468c0 .4.168.696.504.888.336.2.756.3 1.26.3.496 0 .892-.108 1.188-.324a1.01 1.01 0 0 0 .456-.864.888.888 0 0 0-.312-.672c-.208-.184-.592-.292-1.152-.324-.44-.032-.82-.072-1.14-.12-.328.176-.544.364-.648.564-.104.208-.156.392-.156.552ZM487.51 28v-5.952h.912l.06 1.068c.192-.376.468-.672.828-.888.36-.216.768-.324 1.224-.324.704 0 1.264.216 1.68.648.424.424.636 1.08.636 1.968V28h-1.008v-3.372c0-1.24-.512-1.86-1.536-1.86-.512 0-.94.188-1.284.564-.336.368-.504.896-.504 1.584V28h-1.008Zm8.583.144c-.496 0-.908-.084-1.236-.252a1.713 1.713 0 0 1-.972-1.584c0-.608.232-1.076.696-1.404.464-.328 1.096-.492 1.896-.492h1.608v-.072c0-.52-.136-.912-.408-1.176-.272-.272-.636-.408-1.092-.408-.392 0-.732.1-1.02.3-.28.192-.456.476-.528.852h-1.032c.04-.432.184-.796.432-1.092.256-.296.572-.52.948-.672.376-.16.776-.24 1.2-.24.832 0 1.456.224 1.872.672.424.44.636 1.028.636 1.764V28h-.9l-.06-1.068a2.39 2.39 0 0 1-.744.864c-.32.232-.752.348-1.296.348Zm.156-.852c.384 0 .712-.1.984-.3.28-.2.492-.46.636-.78a2.43 2.43 0 0 0 .216-1.008v-.012h-1.524c-.592 0-1.012.104-1.26.312-.24.2-.36.452-.36.756 0 .312.112.564.336.756.232.184.556.276.972.276Zm4.092.708v-8.64h1.008V28h-1.008Zm7.588.144c-.712 0-1.304-.18-1.776-.54-.472-.36-.748-.848-.828-1.464h1.032c.064.312.228.584.492.816.272.224.636.336 1.092.336.424 0 .736-.088.936-.264.2-.184.3-.4.3-.648 0-.36-.132-.6-.396-.72-.256-.12-.62-.228-1.092-.324a6.189 6.189 0 0 1-.96-.276 2.221 2.221 0 0 1-.804-.504c-.216-.224-.324-.516-.324-.876 0-.52.192-.944.576-1.272.392-.336.92-.504 1.584-.504.632 0 1.148.16 1.548.48.408.312.644.76.708 1.344h-.996a1.008 1.008 0 0 0-.396-.708c-.216-.176-.508-.264-.876-.264-.36 0-.64.076-.84.228a.728.728 0 0 0-.288.6c0 .24.124.428.372.564.256.136.6.252 1.032.348.368.08.716.18 1.044.3.336.112.608.284.816.516.216.224.324.552.324.984.008.536-.196.98-.612 1.332-.408.344-.964.516-1.668.516Zm5.538-.144c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm2.025 0v-5.952h.912l.084 1.14c.184-.392.464-.704.84-.936.376-.232.84-.348 1.392-.348v1.056h-.276c-.352 0-.676.064-.972.192-.296.12-.532.328-.708.624-.176.296-.264.704-.264 1.224v3h-1.008Zm6.549.144c-.568 0-1.072-.128-1.512-.384a2.856 2.856 0 0 1-1.044-1.092c-.248-.464-.372-1.012-.372-1.644 0-.624.124-1.168.372-1.632a2.667 2.667 0 0 1 1.032-1.092c.448-.264.964-.396 1.548-.396.576 0 1.072.132 1.488.396.424.256.748.596.972 1.02a2.885 2.885 0 0 1 .324 1.632v.3h-4.74c.024.456.128.836.312 1.14.192.296.428.52.708.672.288.152.592.228.912.228.416 0 .764-.096 1.044-.288.28-.192.484-.452.612-.78h.996a2.705 2.705 0 0 1-.924 1.38c-.448.36-1.024.54-1.728.54Zm0-5.388c-.48 0-.908.148-1.284.444-.368.288-.58.712-.636 1.272h3.744c-.024-.536-.208-.956-.552-1.26-.344-.304-.768-.456-1.272-.456ZM525.933 28v-5.952h.912l.06 1.068c.192-.376.468-.672.828-.888.36-.216.768-.324 1.224-.324.704 0 1.264.216 1.68.648.424.424.636 1.08.636 1.968V28h-1.008v-3.372c0-1.24-.512-1.86-1.536-1.86-.512 0-.94.188-1.284.564-.336.368-.504.896-.504 1.584V28h-1.008Zm8.955-1.872a3.07 3.07 0 0 1-.924-.132l-.6.564c.088.064.196.12.324.168.136.04.328.08.576.12.248.032.596.068 1.044.108.8.056 1.372.24 1.716.552.344.312.516.72.516 1.224 0 .344-.096.672-.288.984-.184.312-.472.568-.864.768-.384.2-.88.3-1.488.3a4.356 4.356 0 0 1-1.428-.216c-.416-.136-.74-.348-.972-.636-.232-.28-.348-.636-.348-1.068 0-.224.06-.468.18-.732.12-.256.344-.5.672-.732a3.022 3.022 0 0 1-.456-.228 2.898 2.898 0 0 1-.336-.288v-.276l1.02-1.008c-.472-.4-.708-.928-.708-1.584 0-.392.092-.748.276-1.068.184-.32.452-.572.804-.756.352-.192.78-.288 1.284-.288.344 0 .656.048.936.144h2.196v.756l-1.116.048c.232.336.348.724.348 1.164 0 .392-.096.748-.288 1.068-.184.32-.452.576-.804.768-.344.184-.768.276-1.272.276Zm0-.828c.424 0 .76-.108 1.008-.324.256-.224.384-.544.384-.96 0-.408-.128-.72-.384-.936-.248-.224-.584-.336-1.008-.336-.432 0-.776.112-1.032.336-.248.216-.372.528-.372.936 0 .416.124.736.372.96.256.216.6.324 1.032.324Zm-1.752 3.468c0 .4.168.696.504.888.336.2.756.3 1.26.3.496 0 .892-.108 1.188-.324a1.01 1.01 0 0 0 .456-.864.888.888 0 0 0-.312-.672c-.208-.184-.592-.292-1.152-.324-.44-.032-.82-.072-1.14-.12-.328.176-.544.364-.648.564-.104.208-.156.392-.156.552Zm8.109-.768c-.544 0-.972-.132-1.284-.396-.312-.264-.468-.74-.468-1.428V22.9h-1.032v-.852h1.032l.132-1.428h.876v1.428h1.752v.852h-1.752v3.276c0 .376.076.632.228.768.152.128.42.192.804.192h.624V28h-.912Zm2.025 0v-8.64h1.008v3.708c.2-.368.484-.652.852-.852a2.398 2.398 0 0 1 1.2-.312c.688 0 1.24.216 1.656.648.416.424.624 1.08.624 1.968V28h-.996v-3.372c0-1.24-.5-1.86-1.5-1.86-.52 0-.956.188-1.308.564-.352.368-.528.896-.528 1.584V28h-1.008Z"
        />
        <path
          stroke="#FC118A"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M30 29.833V18.167m0 0L24.167 24M30 18.166 35.833 24"
        />
        <path
          stroke="#FD88C5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M374 18.167v11.666m0 0L379.833 24M374 29.834 368.167 24"
        />
        <path stroke="#C2CAA8" strokeWidth={2} d="M11 45h548" />
        <circle cx={211} cy={45} r={7} fill="#FC118A" />
        <circle cx={30} cy={45} r={7} fill="#FC118A" />
        <circle cx={227} cy={45} r={7} fill="#FC118A" />
        <circle cx={243} cy={45} r={7} fill="#FD88C5" />
        <circle cx={163} cy={46} r={7} fill="#FD88C5" />
        <circle cx={259} cy={45} r={7} fill="#FC118A" />
        <circle cx={275} cy={45} r={7} fill="#FD88C5" />
        <circle cx={374} cy={45} r={7} fill="#FC118A" />
        <circle cx={374} cy={45} r={7} fill="#fff" fillOpacity={0.5} />
      </svg>
    </div>
  )
)`
  max-width: 577px;
`;


const Container = styled.div`${({theme}) => `
  > *:not(:first-child):not(.flex-section),
  .flex-section > * {
    margin-top: 100px;
  }

  .orange-box {
    overflow: hidden !imporant;
  }

  @media (max-width: 1150px) {
    .flex-section > *,
    .flex-section > *:first-child,
    .flex-section > *:last-child {
      justify-content: flex-start;
      padding: 0;
    }
    .flex-section .orange-box {
      width: 100%;
      max-width: none !important;
    }
    .flex-section .orange-box svg{
      float: right;
    }
  }

  @media (min-width: 860px) and (max-width: 1150px) {
    .flex-section .orange-box svg{
      margin-right: 20px;
    }
  }

  > * {
    flex-grow: 1;
    justify-content: space-around;
  }

  width: 100%;

  h3 {
    margin-bottom: 28px;
  }

  padding-bottom: ${theme.boxPadding.xl10}px;

  .tmsi-waterflow-section .orange-box{
    max-width: none !important;
  }

  .tmsi-waterflow-section .header{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .tmsi-waterflow-section .header > * {
    flex-grow: 1;
  }

  .tmsi-waterflow-section .filler {
    flex-grow: 2;
  }

  .float-right {
    float: right;
  }

  .quick-links {
    margin-top: ${theme.boxPadding.xl}px;
    text-align: right;
    font-size: 0.75rem;
  }
  .quick-links a, 
  .quick-links a:hover{
    font-size: 0.75rem;
  }
`}`;

const OrangeBoxToggle = styled.div`${() => `
  float: right;
  line-height: 2.3625rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;

  > label {
    line-height: 0;
    margin: 0 0.3rem;
  }
`}`;

/// to delete
type SensorWarning = {
  name: string,
  text: string,
  status: 'active'|'dismissed'|'disabled'
};

const WarningsDisplay: React.FC = () => {
  const {sensorArray, sensorsDataStatus} = useSensorsStore(s => s);
  const sensorArrayLoaded = sensorsDataStatus === 'success'
  const [warnings, setWarnings] = useState<SensorWarning[]>([]);
  const {config, saveUserConfig, configLoaded} = useConfigContext();
  const prevSensorArray = usePrevious(sensorArray);
  const prevConfig = usePrevious(config);

  // Non Cell Sensor Warning
  useEffect(function(){
    console.log('[WarningsDisplay] user config', {config, saveUserConfig, configLoaded});

    const warningType = 'non-cell-sensors';
    if(!sensorArrayLoaded || !configLoaded){ return; }
    if(!(prevSensorArray !== sensorArray||config !== prevConfig)){ return; }

    const disabledWarnings = config?.disabledWarnings;
    const nonCellArray = sensorArray?.filter(s => s.sensorType !== 'v3f')?.map(s => s.uid);
    if(!nonCellArray.length){ return; }
    const currentIndex = warnings.findIndex(w => w.name === warningType);
    const sensorWarning: SensorWarning = currentIndex > -1 ? warnings.splice(currentIndex,1)[0] : {
      name: 'non-cell-sensors',
      text: '',
      status: 'active'
    };

    sensorWarning.text = `We have detected non cellular-enabled sensor${nonCellArray.length>1?'s':''} (${nonCellArray.join(', ')}) on your account. Please note that those wont produce outcome on this dashboard`;

    if( disabledWarnings?.[warningType] ){
      sensorWarning.status = 'disabled';
    }

    warnings.splice(0,0, sensorWarning);

    setWarnings([...warnings]);
  },[sensorArray, warnings, sensorArrayLoaded, config, configLoaded, prevConfig, prevSensorArray, saveUserConfig]);

  const currentWarning = useMemo(() => warnings?.find(w => w.status === 'active'),[warnings]);

  const dismissWarning = useCallback(function markWarningAsDismissed(warning: typeof warnings[0]){
    warning.status = 'dismissed';

    setWarnings([...warnings]);
  },[warnings]);

  const disableWarning = useCallback(function markWarningAsDismissed(warningType: string){
    if(!configLoaded){ return; }
    const newConfig = config || {};

    // adjust config
    if(!newConfig.disabledWarnings){
      newConfig.disabledWarnings = {};
    }

    (newConfig.disabledWarnings as any)[warningType] = true;

    saveUserConfig(newConfig);
  }, [config, configLoaded, saveUserConfig]);

  return currentWarning
    ?
    <ErrorMessage>
      <Flex8>
        <FiAlertCircle />
        <p>{currentWarning.text}</p>
      </Flex8>
      <Flex4>
        <Button onClick={() => dismissWarning(currentWarning)} variant='plain'>Dismiss</Button>

        <Button onClick={() => {
          disableWarning(currentWarning.name);
        }} variant='plain'>Don't show again</Button>
      </Flex4>

    </ErrorMessage>
    :
    null;
};
const ErrorMessage = styled.div`${({theme}) => `
  border-radius: ${theme.boxPadding.xl}px;
  background-color: rgba(${theme.colorsRgb.red.base.toString()}, 0.5);
  padding: ${theme.boxPadding.l}px ${theme.boxPadding.xl5}px;
  border: none;
  margin-top: ${theme.boxMargins.l}px;

  position: relative;

  font-weight: bold;
  color: ${theme.colors.red.dark};

  > :not(svg) {
    margin: ${theme.boxMargins.s}px 0;
  }
  > svg:last-child {
    width: ${theme.boxPadding.l*2}px;
    height: ${theme.boxPadding.l*2}px;
    color: rgba(${theme.colorsRgb.sky.white.toString()},0.5);
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    right: ${theme.boxPadding.xl5}px;
  }

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;

  p {
    display: inline-block
  }
  button {
    float: right;
    flex-shrink: 0;
  }

  svg {
    width: ${theme.fontSize.xl4}px;
    height: ${theme.fontSize.xl4}px;
    vertical-align: middle;
    margin-top: -${theme.fontSize.s/4}px;
    margin-right: ${theme.fontSize.xl}px;
    flex-shrink: 0;
  }

  > * {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }
`}`;



